/**
 * Admin impersonation control panel React component
 * 
 * Displays:
 * - Whether an impersonation session is currently active
 * - The customer being impersonated
 * - Time remaining until session expiry
 * - Button to end the impersonation session
 * - Warning banner indicating data is being viewed as customer
 * 
 * Used by admin routes to show impersonation status and controls.
 * 
 * Related: Issue #281, Issue #302
 */

import React, { useState, useEffect } from 'react';

/**
 * Props for ImpersonationPanel component
 */
interface ImpersonationPanelProps {
  /** UUID of the customer being impersonated */
  impersonatedUserId?: string;
  /** Customer name or email (for display) */
  impersonatedUserName?: string;
  /** Unix timestamp (milliseconds) when session expires */
  expiresAt?: number;
  /** Session ID for revocation */
  sessionId?: string;
  /** Callback when ending session */
  onEndSession?: () => Promise<void>;
  /** Whether the end session request is in progress */
  isLoading?: boolean;
  /** Error message if end session failed */
  error?: string;
}

/**
 * Calculate time remaining until expiry
 * 
 * @param expiresAt - Unix timestamp in milliseconds
 * @returns Formatted time remaining string (e.g., "15 minutes", "2 seconds")
 */
function formatTimeRemaining(expiresAt: number): string {
  const now = Date.now();
  const remaining = Math.max(0, expiresAt - now);
  
  const totalSeconds = Math.floor(remaining / 1000);
  
  if (totalSeconds < 60) {
    return `${totalSeconds} second${totalSeconds !== 1 ? 's' : ''}`;
  }
  
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
  
  const hours = Math.floor(minutes / 60);
  return `${hours} hour${hours !== 1 ? 's' : ''}`;
}

/**
 * ImpersonationPanel component
 * 
 * Shows impersonation status and allows admin to end session.
 * Only renders if there is an active impersonation session.
 */
export function ImpersonationPanel({
  impersonatedUserId,
  impersonatedUserName,
  expiresAt,
  sessionId,
  onEndSession,
  isLoading = false,
  error,
}: ImpersonationPanelProps): React.ReactElement | null {
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [isExpired, setIsExpired] = useState(false);
  
  // Update time remaining every second
  useEffect(() => {
    if (!expiresAt) {
      setTimeRemaining('');
      return;
    }
    
    const updateTimer = () => {
      const now = Date.now();
      if (now >= expiresAt) {
        setIsExpired(true);
        setTimeRemaining('Expired');
      } else {
        setTimeRemaining(formatTimeRemaining(expiresAt));
        setIsExpired(false);
      }
    };
    
    updateTimer();
    
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);
  
  // Don't render if no impersonation session
  if (!impersonatedUserId) {
    return null;
  }
  
  return (
    <div className="impersonation-panel" style={styles.container}>
      <div style={styles.banner}>
        <div style={styles.content}>
          <h3 style={styles.title}>Admin Impersonation Active</h3>
          
          <div style={styles.details}>
            <p style={styles.text}>
              You are viewing data as: <strong>{impersonatedUserName || impersonatedUserId}</strong>
            </p>
            
            <p style={styles.text}>
              Session expires in: <strong style={isExpired ? styles.expiredText : styles.expiryText}>
                {timeRemaining}
              </strong>
            </p>
            
            {sessionId && (
              <p style={styles.sessionId}>
                Session ID: <code>{sessionId}</code>
              </p>
            )}
          </div>
          
          {error && (
            <p style={styles.error}>{error}</p>
          )}
          
          <button
            style={styles.button}
            onClick={onEndSession}
            disabled={isLoading || isExpired}
            type="button"
          >
            {isLoading ? 'Ending Session...' : 'End Impersonation'}
          </button>
        </div>
        
        <div style={styles.warningIcon}>⚠️</div>
      </div>
    </div>
  );
}

/**
 * Inline styles for the impersonation panel
 * Uses a professional warning/alert color scheme
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    marginBottom: '20px',
  },
  
  banner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#fef3c7', // Warm yellow warning color
    border: '2px solid #f59e0b', // Amber border
    borderRadius: '8px',
    padding: '16px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  
  content: {
    flex: 1,
  },
  
  title: {
    margin: '0 0 12px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#92400e',
  },
  
  details: {
    marginBottom: '12px',
  },
  
  text: {
    margin: '8px 0',
    fontSize: '14px',
    color: '#78350f',
  },
  
  expiryText: {
    color: '#92400e',
    fontWeight: '600',
  },
  
  expiredText: {
    color: '#dc2626',
    fontWeight: '600',
  },
  
  sessionId: {
    margin: '8px 0',
    fontSize: '12px',
    color: '#92400e',
    fontFamily: 'monospace',
  },
  
  error: {
    margin: '8px 0',
    fontSize: '13px',
    color: '#991b1b',
    backgroundColor: '#fee2e2',
    padding: '8px 12px',
    borderRadius: '4px',
    borderLeft: '3px solid #dc2626',
  },
  
  button: {
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 200ms',
  },
  
  warningIcon: {
    fontSize: '24px',
    marginLeft: '16px',
    minWidth: '32px',
    textAlign: 'center',
  },
};

/**
 * Hook to manage impersonation session state
 * 
 * @returns Object with impersonation state and controls
 */
export function useImpersonationSession() {
  const [sessionState, setSessionState] = React.useState<{
    impersonatedUserId?: string;
    impersonatedUserName?: string;
    expiresAt?: number;
    sessionId?: string;
    isLoading: boolean;
    error?: string;
  }>({
    isLoading: false,
  });
  
  const endSession = React.useCallback(async () => {
    if (!sessionState.sessionId) {
      return;
    }
    
    setSessionState((prev) => ({ ...prev, isLoading: true, error: undefined }));
    
    try {
      // TODO: Call POST /api/admin/impersonate/end endpoint
      // const response = await fetch('/api/admin/impersonate/end', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ sessionId: sessionState.sessionId }),
      // });
      
      // if (!response.ok) {
      //   throw new Error('Failed to end session');
      // }
      
      // Clear session state on success
      setSessionState({
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setSessionState((prev) => ({ ...prev, isLoading: false, error: message }));
    }
  }, [sessionState.sessionId]);
  
  return {
    ...sessionState,
    setSessionState,
    endSession,
  };
}

/**
 * Example usage:
 *
 * function AdminDashboard() {
 *   const impersonation = useImpersonationSession();
 *
 *   return (
 *     <ImpersonationPanel
 *       impersonatedUserId={impersonation.impersonatedUserId}
 *       impersonatedUserName={impersonation.impersonatedUserName}
 *       expiresAt={impersonation.expiresAt}
 *       sessionId={impersonation.sessionId}
 *       onEndSession={impersonation.endSession}
 *       isLoading={impersonation.isLoading}
 *       error={impersonation.error}
 *     />
 *   );
 * }
 */
